import { Product } from '../types/product';

/**
 * Termék adatokat dokumentum változókká alakítja
 */
export const productToDocumentVariables = (product: Product): Record<string, string> => {
  const variables: Record<string, string> = {};
  
  // Alapvető termék adatok
  variables['PRODUCT_NAME'] = product.name || '';
  variables['PRODUCT_CODE'] = product.code || '';
  variables['PRODUCT_DESCRIPTION'] = product.description || '';
  variables['PRODUCT_TYPE'] = getProductTypeLabel(product.type);
  variables['PRODUCT_UNIT'] = product.unit || '';
  
  // Árazás
  if (product.netPrice) {
    variables['PRODUCT_NET_PRICE'] = formatCurrency(product.netPrice);
  }
  if (product.grossPrice) {
    variables['PRODUCT_GROSS_PRICE'] = formatCurrency(product.grossPrice);
  }
  if (product.purchasePrice) {
    variables['PRODUCT_PURCHASE_PRICE'] = formatCurrency(product.purchasePrice);
  }
  
  variables['PRODUCT_TAX_RATE'] = product.taxRate || '';
  
  // Kategória
  if (product.category) {
    variables['PRODUCT_CATEGORY'] = product.category.fullPath || 
      [product.category.level1, product.category.level2, product.category.level3]
        .filter(Boolean).join(' > ');
  }
  
  // Csomag specifikus adatok
  if (product.type === 'package' && product.packageDetails) {
    const pkg = product.packageDetails;
    
    // Szolgáltatások listája
    variables['PACKAGE_SERVICES_LIST'] = pkg.services
      .map(service => {
        let serviceText = service.name;
        if (service.hours && service.hourlyRate) {
          serviceText += ` (${service.hours} óra × ${formatCurrency(service.hourlyRate)})`;
        } else if (service.quantity && service.unit) {
          serviceText += ` (${service.quantity} ${service.unit})`;
        }
        return serviceText;
      })
      .join(', ');
    
    // Szolgáltatások táblázat (HTML)
    variables['PACKAGE_SERVICES_TABLE'] = generateServicesTable(pkg.services);
    
    // Összesítő adatok
    variables['PACKAGE_TOTAL_HOURS'] = pkg.totalHours?.toString() || '';
    variables['PACKAGE_ESTIMATED_DURATION'] = pkg.estimatedDuration || '';
    variables['PACKAGE_METHODOLOGY'] = pkg.methodology || '';
    
    // Szállítandók
    if (pkg.deliverables && pkg.deliverables.length > 0) {
      variables['PACKAGE_DELIVERABLES'] = pkg.deliverables.join(', ');
      variables['PACKAGE_DELIVERABLES_LIST'] = pkg.deliverables
        .map(item => `• ${item}`)
        .join('\n');
    }
    
    // Követelmények
    if (pkg.requirements && pkg.requirements.length > 0) {
      variables['PACKAGE_REQUIREMENTS'] = pkg.requirements.join(', ');
      variables['PACKAGE_REQUIREMENTS_LIST'] = pkg.requirements
        .map(item => `• ${item}`)
        .join('\n');
    }
    
    // Ütemterv
    if (pkg.timeline && pkg.timeline.length > 0) {
      variables['PACKAGE_TIMELINE'] = pkg.timeline
        .map(phase => `${phase.phase} (${phase.duration}): ${phase.description}`)
        .join('\n');
      
      variables['PACKAGE_TIMELINE_TABLE'] = generateTimelineTable(pkg.timeline);
    }
    
    // Számított értékek
    const totalServicePrice = pkg.services.reduce((sum, service) => {
      if (service.hours && service.hourlyRate) {
        return sum + (service.hours * service.hourlyRate);
      } else if (service.price) {
        return sum + service.price;
      }
      return sum;
    }, 0);
    
    if (totalServicePrice > 0) {
      variables['PACKAGE_SERVICES_TOTAL'] = formatCurrency(totalServicePrice);
    }
  }
  
  // Egyéb adatok
  if (product.manufacturer) {
    variables['PRODUCT_MANUFACTURER'] = product.manufacturer;
  }
  
  if (product.warrantyDays) {
    variables['PRODUCT_WARRANTY'] = `${product.warrantyDays} nap`;
  }
  
  if (product.barcode) {
    variables['PRODUCT_BARCODE'] = product.barcode;
  }
  
  if (product.articleNumber) {
    variables['PRODUCT_ARTICLE_NUMBER'] = product.articleNumber;
  }
  
  return variables;
};

/**
 * Termék típus magyar elnevezése
 */
const getProductTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    'product': 'Termék',
    'service': 'Szolgáltatás',
    'package': 'Szolgáltatási csomag',
    'digital': 'Digitális termék',
    'subscription': 'Előfizetés'
  };
  return typeLabels[type] || type;
};

/**
 * Pénzösszeg formázása
 */
const formatCurrency = (amount: number, currency: string = 'HUF'): string => {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

/**
 * Szolgáltatások táblázat generálása HTML formátumban
 */
const generateServicesTable = (services: any[]): string => {
  let table = '<table border="1" style="border-collapse: collapse; width: 100%;">';
  table += '<tr><th>Szolgáltatás</th><th>Mennyiség</th><th>Egységár</th><th>Összesen</th></tr>';
  
  services.forEach(service => {
    const quantity = service.hours || service.quantity || 1;
    const unit = service.hours ? 'óra' : (service.unit || 'db');
    const unitPrice = service.hourlyRate || service.price || 0;
    const total = quantity * unitPrice;
    
    table += `<tr>
      <td>${service.name}</td>
      <td>${quantity} ${unit}</td>
      <td>${formatCurrency(unitPrice)}</td>
      <td>${formatCurrency(total)}</td>
    </tr>`;
  });
  
  table += '</table>';
  return table;
};

/**
 * Ütemterv táblázat generálása HTML formátumban
 */
const generateTimelineTable = (timeline: any[]): string => {
  let table = '<table border="1" style="border-collapse: collapse; width: 100%;">';
  table += '<tr><th>Fázis</th><th>Időtartam</th><th>Leírás</th></tr>';
  
  timeline.forEach(phase => {
    table += `<tr>
      <td>${phase.phase}</td>
      <td>${phase.duration}</td>
      <td>${phase.description}</td>
    </tr>`;
  });
  
  table += '</table>';
  return table;
};

export default productToDocumentVariables;
